/** Tool definitions: Transaction reads and manual writes. */

import type { ToolDefinition } from './types.js';

export const transactionsTools = {
  get_recent_transactions: {
    name: 'get_recent_transactions',
    access: 'read',
    description:
      'Get recent transactions with optional filtering by symbol, transaction type (buy, sell, dividend, etc.), or account. Returns transaction details including date, symbol, quantity, price, and fees. Limit to last 50 transactions by default.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to fetch transactions for.',
        },
        accountIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional array of account IDs to filter transactions.',
        },
        symbol: {
          type: 'string',
          description: 'Optional symbol to filter transactions (e.g., "AAPL").',
        },
        transactionType: {
          type: 'string',
          description: 'Optional transaction type filter (e.g., "buy", "sell", "dividend", "deposit").',
        },
        limit: {
          type: 'number',
          description: 'Maximum number of transactions to return. Defaults to 50, max 200.',
        },
      },
      required: ['portfolioId'],
    },
  },
  get_transactions_diagnostic: {
    name: 'get_transactions_diagnostic',
    access: 'read',
    description:
      'DEBUG: Get DB transactions with full sync metadata (externalId, classificationStatus, importSource, importedAt, securityType, tradeDate, settlementDate). Use to investigate missing or mis-classified transactions. Optional date range, symbol, or externalId filter.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to query.',
        },
        accountIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional account IDs to filter. If omitted, includes all accounts in the portfolio.',
        },
        symbol: {
          type: 'string',
          description: 'Optional symbol filter (e.g., "QQQ"). Matches exactly (case-insensitive).',
        },
        externalId: {
          type: 'string',
          description: 'Optional SnapTrade externalId to look up a single transaction.',
        },
        startDate: {
          type: 'string',
          description: 'Optional ISO date (YYYY-MM-DD) lower bound.',
        },
        endDate: {
          type: 'string',
          description: 'Optional ISO date (YYYY-MM-DD) upper bound.',
        },
        limit: {
          type: 'number',
          description: 'Max transactions to return. Defaults to 200, max 1000.',
        },
      },
      required: ['portfolioId'],
    },
  },
  create_transaction: {
    name: 'create_transaction',
    access: 'write',
    description:
      'Create a manual transaction in an account. Use to backfill data the broker did not relay (e.g., option assignments, missed buys). Allowed on SnapTrade-connected accounts: subsequent syncs are additive and signature-dedup, so manual entries are stable. Required: accountId, action, date, amount.',
    inputSchema: {
      type: 'object',
      properties: {
        accountId: { type: 'string', description: 'Luminaria account UUID.' },
        action: {
          type: 'string',
          description: 'Transaction action. Valid values: buy, sell, dividend, reinvestment, interest, fpl_income, transfer_in, transfer_out, deposit, withdrawal, sweep_in, sweep_out, split, merger, spinoff, redemption, redemption_cancel, option_assignment, option_exercise, option_expiration, buy_to_open, buy_to_close, sell_to_open, sell_to_close, fee, cash_adjustment, journal.',
        },
        date: { type: 'string', description: 'ISO date (YYYY-MM-DD). Effective/posting date.' },
        tradeDate: { type: 'string', description: 'Optional trade date (YYYY-MM-DD).' },
        settlementDate: { type: 'string', description: 'Optional settlement date (YYYY-MM-DD).' },
        symbol: { type: 'string', description: 'Stock ticker or OCC option symbol.' },
        description: { type: 'string', description: 'Free-form description.' },
        quantity: { type: 'number', description: 'Share / contract count. Positive number; sign is implied by action.' },
        price: { type: 'number', description: 'Per-share / per-contract price.' },
        amount: { type: 'number', description: 'Total cash impact. Negative for cash out (buy), positive for cash in (sell, dividend).' },
        fees: { type: 'number', description: 'Fees paid.' },
        securityType: {
          type: 'string',
          description: 'Optional security type override (stock, etf, bond, mutual_fund, money_market, cash, long_call, short_call, long_put, short_put, option, crypto, unknown).',
        },
        excludeFromCostBasis: { type: 'boolean', description: 'If true, the transaction is excluded from cost basis calculations.' },
        cashFlowType: { type: 'string', enum: ['deposit', 'withdrawal'], description: 'External cash flow classification for return calculations.' },
      },
      required: ['accountId', 'action', 'date', 'amount'],
    },
  },
  update_transaction: {
    name: 'update_transaction',
    access: 'write',
    description:
      'Update fields on an existing transaction. Only the provided fields are changed. Use to correct mis-classified options, fix amounts, or adjust dates.',
    inputSchema: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'Transaction UUID.' },
        action: { type: 'string', description: 'New action (see create_transaction for valid values).' },
        symbol: { type: 'string' },
        description: { type: 'string' },
        quantity: { type: 'number' },
        price: { type: 'number' },
        amount: { type: 'number' },
        fees: { type: 'number' },
        date: { type: 'string', description: 'ISO date (YYYY-MM-DD).' },
        tradeDate: { type: 'string', description: 'ISO date (YYYY-MM-DD).' },
        settlementDate: { type: 'string', description: 'ISO date (YYYY-MM-DD).' },
        securityType: { type: 'string' },
        excludeFromCostBasis: { type: 'boolean' },
        cashFlowType: { type: 'string', enum: ['deposit', 'withdrawal'] },
      },
      required: ['id'],
    },
  },
  delete_transaction: {
    name: 'delete_transaction',
    access: 'write',
    description: 'Delete a transaction by ID. Triggers cost basis and tax cache invalidation.',
    inputSchema: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'Transaction UUID.' },
      },
      required: ['id'],
    },
  },
} satisfies Record<string, ToolDefinition>;
